import { useState } from "react";
import { useUsageLimit } from "../../../customhooks/useUsageLimit.js";
import { useAuth } from "../../Login/AuthContext.js";
import { CircularProgress } from "@mui/material";
import operationIcon from "../../../assets/math-operations.svg";
import numbersIcon from "../../../assets/numbers.png";
import glitterIcon from "../../../assets/glitter.svg";
import "./CountingQuestions.css";
import PromptPreview from "../../CustomPrompt/PromptPreview.jsx";

// Toggle which parts of the worksheet config are shown in the preview box
const PROMPT_PARTS = {
  template: true,         // Template name
  questionSettings: true, // Number of questions + max pictures per cell
  mediaSelection: true,   // Selected emojis
};

function buildPromptPreview(questionCount, maxPictures, selectedEmojis) {
  const lines = [];
  if (PROMPT_PARTS.template)
    lines.push('Template: Counting Worksheet');
  if (PROMPT_PARTS.questionSettings) {
    lines.push(`Questions: ${questionCount}`);
    lines.push(`Max pictures per cell: ${maxPictures}`);
  }
  if (PROMPT_PARTS.mediaSelection)
    lines.push(`Emojis: ${selectedEmojis.length > 0 ? selectedEmojis.join(' ') : '[none selected]'}`);
  return lines.join('\n');
}

const emojiSet = [
  "🍎", "🐶", "🚗", "🌸", "⭐", "⚽",
  "🍌", "🐠", "🎈", "🦋", "🍓", "🐸",
  "🦄", "🚀", "🍕", "🐧", "🎵", "🍩"
];

const CountingWorksheetTemplate = ({ generateContentAndPdf, loading }) => {
  const [uploadedImages] = useState([]);
  const [selectedEmojis, setSelectedEmojis] = useState([]);
  const [questionCount, setQuestionCount] = useState(6);
  const [maxPictures, setMaxPictures] = useState(15);

  const promptPreview = buildPromptPreview(questionCount, maxPictures, selectedEmojis);

  const { isAuthenticated } = useAuth();
  const { remainingUses, isDisabled, incrementUsage } = useUsageLimit(
    "globalUsage",
    5,
    undefined,
    isAuthenticated
  );

  const [errors, setErrors] = useState({
    imageError: "",
    questionError: "",
    maxError: "",
  });

  const toggleEmoji = (emoji) => {
    let updated;
    if (selectedEmojis.includes(emoji)) {
      updated = selectedEmojis.filter((e) => e !== emoji);
    } else {
      updated = [...selectedEmojis, emoji];
    }
    if (uploadedImages.length + updated.length <= 6) {
      setSelectedEmojis(updated);
      if (updated.length > 0) {
        setErrors((prev) => ({ ...prev, imageError: "" }));
      }
    }
  };

  const submitHandler = (e) => {
    e.preventDefault();
    let hasError = false;
    const newErrors = {};

    if (uploadedImages.length + selectedEmojis.length === 0) {
      newErrors.imageError = "Please upload or select at least one image/emoji.";
      hasError = true;
    }
    if (!questionCount) {
      newErrors.questionError = "Please choose number of questions.";
      hasError = true;
    }
    if (!maxPictures) {
      newErrors.maxError = "Please choose max pictures per cell.";
      hasError = true;
    }

    if (!isAuthenticated && isDisabled) {
      alert("You have reached the maximum number of uses. Please sign up for free credits! 👉🏻");
      return;
    }

    if (hasError) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    if (!isAuthenticated) incrementUsage();

    const payload = {
      template: "counting",
      worksheetName: "Counting Worksheet",
      options: {
        numQuestions: questionCount,
        maxPicturesPerCell: maxPictures,
        uploadedImages: uploadedImages.map((f) => ({
          name: f.name,
          url: URL.createObjectURL(f),
        })),
        selectedEmojis: selectedEmojis,
      },
    };
    generateContentAndPdf(payload);
  };

  return (
    <div className="counting-template column-questions">

         <PromptPreview text={promptPreview} />

      {/* Emoji picker — 6 columns = 3 rows */}
      <div className="input-group">
        <label className="form-label">Pick emojis:</label>
        <div className="emoji-grid">
          {emojiSet.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className={`emoji-btn ${selectedEmojis.includes(emoji) ? "selected" : ""}`}
              onClick={() => toggleEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      </div>
      {errors.imageError && <p className="error">{errors.imageError}</p>}

      {/* Params row: controls left, generate button right */}
      <div className="ct-params-row">
        <div className="ct-param">
          <label className="form-label">
            <img src={numbersIcon} alt="info" style={{ width: "16px", height: "16px" }} />
            Number of Questions:
          </label>
          <select
            className="operation-dropdown"
            value={questionCount || ""}
            onChange={(e) => { setQuestionCount(e.target.value); setErrors((prev) => ({ ...prev, questionError: "" })); }}
          >
            <option value="" disabled>Select...</option>
            {[6, 8, 10].map((num) => <option key={num} value={num}>{num}</option>)}
          </select>
        </div>

        <div className="ct-param">
          <label className="form-label">
            <img src={operationIcon} alt="info" style={{ width: "16px", height: "16px" }} />
            Max Pictures per Cell:
          </label>
          <select
            className="operation-dropdown"
            value={maxPictures || ""}
            onChange={(e) => { setMaxPictures(e.target.value); setErrors((prev) => ({ ...prev, maxError: "" })); }}
          >
            <option value="" disabled>Select...</option>
            {[5, 10, 15, 20].map((num) => <option key={num} value={num}>{num}</option>)}
          </select>
        </div>

        <button
          data-gtm-event="track-click"
          gtm-data-label="generated-counting-questions-temp"
          className="generate-button"
          onClick={submitHandler}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" style={{ marginRight: "8px" }} />
          ) : (
            <img src={glitterIcon} alt="sparkle" style={{ width: "16px", height: "16px", marginRight: "5px" }} />
          )}
          {loading ? "Generating..." : isAuthenticated ? "Generate Questions" : `Generate (${remainingUses} remaining)`}
        </button>
      </div>

   
    </div>
  );
};

export default CountingWorksheetTemplate;
